import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api/client';
import type { TimeEntry, Project } from '../types';

export default function TimePage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Partial<TimeEntry> | null>(null);

  const { data: entries = [], isLoading } = useQuery<TimeEntry[]>({
    queryKey: ['time'],
    queryFn: () => api.get('/time').then((r) => r.data),
  });

  const { data: projects = [] } = useQuery<Project[]>({
    queryKey: ['projects'],
    queryFn: () => api.get('/projects').then((r) => r.data),
  });

  const saveMutation = useMutation({
    mutationFn: (e: Partial<TimeEntry>) =>
      e.id ? api.put(`/time/${e.id}`, e) : api.post('/time', e),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['time'] }); setEditing(null); },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/time/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['time'] }),
  });

  const stopMutation = useMutation({
    mutationFn: (id: string) => api.put(`/time/${id}/stop`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['time'] }),
  });

  const fmtDate = (s: string) =>
    new Date(s).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });

  const duration = (e: TimeEntry) => {
    if (!e.endedAt) return null;
    const mins = Math.round((new Date(e.endedAt).getTime() - new Date(e.startedAt).getTime()) / 60000) - e.breakMin;
    return `${Math.floor(mins / 60)}h ${mins % 60}min`;
  };

  const totalHours = entries.reduce((sum, e) => {
    if (!e.endedAt) return sum;
    const mins = Math.round((new Date(e.endedAt).getTime() - new Date(e.startedAt).getTime()) / 60000) - e.breakMin;
    return sum + mins / 60;
  }, 0);

  return (
    <div>
      <div className="sp-page-header">
        <div>
          <h1 className="sp-page-title">Zeiterfassung</h1>
          <p className="sp-page-subtitle">
            {entries.length} Einträge &middot; {totalHours.toFixed(1)}h gesamt
          </p>
        </div>
        <button onClick={() => setEditing({ projectId: '', startedAt: new Date().toISOString() })} className="sp-btn-primary">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Neuer Eintrag
        </button>
      </div>

      {editing && (
        <div className="sp-form-card">
          <h3 className="sp-form-title">{editing.id ? 'Eintrag bearbeiten' : 'Neuer Zeiteintrag'}</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="sp-label">Projekt *</label>
              <select value={editing.projectId || ''} onChange={(e) => setEditing({ ...editing, projectId: e.target.value })}
                className="sp-input">
                <option value="">Projekt wählen...</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
              </select>
            </div>
            <div>
              <label className="sp-label">Start</label>
              <input type="datetime-local" value={(editing.startedAt || '').slice(0, 16)}
                onChange={(e) => setEditing({ ...editing, startedAt: new Date(e.target.value).toISOString() })}
                className="sp-input" />
            </div>
            <div>
              <label className="sp-label">Ende</label>
              <input type="datetime-local" value={(editing.endedAt || '').slice(0, 16)}
                onChange={(e) => setEditing({ ...editing, endedAt: new Date(e.target.value).toISOString() })}
                className="sp-input" />
            </div>
            <div>
              <label className="sp-label">Pause (Min.)</label>
              <input type="number" value={editing.breakMin ?? 0}
                onChange={(e) => setEditing({ ...editing, breakMin: +e.target.value })} className="sp-input" />
            </div>
            <div className="col-span-2">
              <label className="sp-label">Notiz</label>
              <input value={editing.note || ''} onChange={(e) => setEditing({ ...editing, note: e.target.value })}
                className="sp-input" placeholder="Optionale Notiz" />
            </div>
          </div>
          <div className="sp-form-actions">
            <button onClick={() => saveMutation.mutate(editing)} disabled={saveMutation.isPending} className="sp-btn-primary">
              {saveMutation.isPending ? 'Speichern...' : 'Speichern'}
            </button>
            <button onClick={() => setEditing(null)} className="sp-btn-secondary">Abbrechen</button>
          </div>
        </div>
      )}

      <div className="sp-card overflow-hidden animate-fade-in">
        <table className="sp-table">
          <thead>
            <tr>
              <th>Projekt</th>
              <th>Mitarbeiter</th>
              <th>Start</th>
              <th>Dauer</th>
              <th>Notiz</th>
              <th className="w-28 text-right">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={6}><div className="flex items-center justify-center py-12 gap-2 text-slate-400">
                <svg className="sp-spinner" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" /></svg>
                Laden...</div></td></tr>
            ) : entries.length === 0 ? (
              <tr><td colSpan={6}><div className="sp-empty">
                <div className="w-12 h-12 rounded-2xl bg-surface-100 flex items-center justify-center mb-3">
                  <svg className="w-6 h-6 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                </div>
                <span className="text-sm font-medium">Keine Zeiteinträge vorhanden</span>
              </div></td></tr>
            ) : entries.map((e) => (
              <tr key={e.id} onClick={() => setEditing(e)} className="cursor-pointer hover:bg-brand-50/50 transition-colors">
                <td className="font-medium text-slate-800">{e.project?.title || '-'}</td>
                <td className="text-slate-500">{e.user?.name || '-'}</td>
                <td className="font-mono text-xs text-slate-500">{fmtDate(e.startedAt)}</td>
                <td>
                  {duration(e) ? (
                    <span className="sp-badge bg-brand-50 text-brand-700 border border-brand-200">{duration(e)}</span>
                  ) : (
                    <span className="sp-badge bg-emerald-50 text-emerald-700 border border-emerald-200">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse-dot" />
                      Läuft
                    </span>
                  )}
                </td>
                <td className="text-slate-400 text-xs">{e.note || <span className="text-slate-300">-</span>}</td>
                <td className="text-right">
                  <div className="flex items-center justify-end gap-0.5">
                    {!e.endedAt && (
                      <button onClick={() => stopMutation.mutate(e.id)}
                        className="sp-btn-icon !text-amber-500 hover:!bg-amber-50" title="Stoppen">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                        </svg>
                      </button>
                    )}
                    <button onClick={() => setEditing(e)} className="sp-btn-icon" title="Bearbeiten">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button onClick={() => { if (confirm('Zeiteintrag löschen?')) deleteMutation.mutate(e.id); }}
                      className="sp-btn-icon hover:!text-error-500 hover:!bg-error-50" title="Löschen">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
